import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Card,
  CardContent,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Security as SecurityIcon,
  Shield as ShieldIcon,
  Lock as LockIcon,
  History as HistoryIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Help as HelpIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import securityService, { SecurityStatus, SecurityEvent } from '../services/securityService';
import { safeLength, safeDateFormat } from '../utils/safeAccess';

const SecurityDashboard: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [securityStatus, setSecurityStatus] = useState<SecurityStatus | null>(null);
  const [securityEvents, setSecurityEvents] = useState<SecurityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSecurityData();
  }, []);

  const loadSecurityData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load security status and events in parallel
      const [status, events] = await Promise.all([
        securityService.getSecurityStatus(),
        securityService.getSecurityEvents()
      ]);

      setSecurityStatus(status);
      setSecurityEvents(events);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load security data');
    } finally {
      setLoading(false);
    }
  };

  const getEventIcon = (eventType: string, success: boolean) => {
    if (!success) return <ErrorIcon color="error" />;

    switch (eventType) {
      case 'LOGIN_SUCCESS':
        return <CheckIcon color="success" />;
      case 'MFA_ENABLED':
      case 'MFA_LOGIN_SUCCESS':
        return <ShieldIcon color="success" />;
      case 'PASSWORD_CHANGED':
        return <LockIcon color="info" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const getEventColor = (eventType: string) => {
    switch (eventType) {
      case 'LOGIN_SUCCESS':
        return 'success';
      case 'LOGIN_FAILED':
        return 'error';
      case 'MFA_ENABLED':
      case 'MFA_LOGIN_SUCCESS':
        return 'success';
      case 'ACCOUNT_LOCKED':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (!user) {
    return (
      <Container sx={{ py: 8 }}>
        <Typography>You must be logged in to view this page.</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 4
      }}>
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              color: '#003366',
              fontWeight: 700,
              mb: 1
            }}
            tabIndex={0}
            aria-label="Security Dashboard"
          >
            <SecurityIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            Security Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Monitor your account security and manage security settings
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh security data">
            <IconButton
              onClick={loadSecurityData}
              disabled={loading}
              aria-label="Refresh security data"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Security settings">
            <IconButton aria-label="Security settings">
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Security help">
            <IconButton aria-label="Security help">
              <HelpIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }} aria-live="assertive">
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <>
          {/* Status Overview Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* MFA Status Card */}
            <Grid item xs={12} md={4}>
              <Card sx={{
                borderLeft: '4px solid #003366',
                height: '100%'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ShieldIcon sx={{ color: '#003366', mr: 1 }} />
                    <Typography variant="h6" sx={{ color: '#003366', fontWeight: 600 }}>
                      Multi-Factor Authentication
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={securityStatus?.mfaEnabled ? 'ENABLED' : 'DISABLED'}
                      color={securityStatus?.mfaEnabled ? 'success' : 'warning'}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>

                  {securityStatus?.mfaEnabled && (
                    <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                      <Typography variant="body2">
                        Backup codes remaining: {securityStatus?.backupCodesRemaining || 0}
                      </Typography>
                      {securityStatus?.mfaLastUsed && (
                        <Typography variant="body2">
                          Last used: {new Date(securityStatus.mfaLastUsed).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Password Status Card */}
            <Grid item xs={12} md={4}>
              <Card sx={{
                borderLeft: '4px solid #003366',
                height: '100%'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LockIcon sx={{ color: '#003366', mr: 1 }} />
                    <Typography variant="h6" sx={{ color: '#003366', fontWeight: 600 }}>
                      Password Security
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label="SECURE"
                      color="success"
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>

                  <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                    <Typography variant="body2">
                      Last changed: {safeDateFormat(securityStatus?.passwordLastChanged)}
                    </Typography>
                    {securityStatus?.passwordExpiresAt && (
                      <Typography variant="body2">
                        Expires: {safeDateFormat(securityStatus.passwordExpiresAt)}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Account Status Card */}
            <Grid item xs={12} md={4}>
              <Card sx={{
                borderLeft: '4px solid #003366',
                height: '100%'
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SecurityIcon sx={{ color: '#003366', mr: 1 }} />
                    <Typography variant="h6" sx={{ color: '#003366', fontWeight: 600 }}>
                      Account Status
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={securityStatus?.accountLocked ? 'LOCKED' : 'NORMAL'}
                      color={securityStatus?.accountLocked ? 'error' : 'success'}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>

                  <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                    <Typography variant="body2">
                      Failed attempts: {securityStatus?.failedLoginAttempts || 0}
                    </Typography>
                    {securityStatus?.lastLoginAt && (
                      <Typography variant="body2">
                        Last login: {safeDateFormat(securityStatus.lastLoginAt)}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Tabs for different views */}
          <Paper sx={{ width: '100%', mb: 4 }}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              aria-label="Security dashboard tabs"
            >
              <Tab label="Security Events" />
              <Tab label="MFA Management" />
              <Tab label="Password Management" />
            </Tabs>

            {/* Security Events Tab */}
            {activeTab === 0 && (
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#003366', fontWeight: 600 }}>
                  Recent Security Events
                </Typography>

                {!securityEvents || safeLength(securityEvents) === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No security events found.
                  </Typography>
                ) : (
                  <List>
                    {securityEvents?.map((event, index) => (
                      <React.Fragment key={event?.id || index}>
                        <ListItem>
                          <ListItemIcon>
                            {getEventIcon(event?.eventType, event?.success)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {event?.description || 'Unknown event'}
                                </Typography>
                                <Chip
                                  label={(event?.eventType || '').replace(/_/g, ' ')}
                                  size="small"
                                  color={getEventColor(event?.eventType) as any}
                                />
                              </Box>
                            }
                            secondary={
                              <Typography variant="body2" color="text.secondary">
                                {event?.timestamp ? new Date(event.timestamp).toLocaleString() : 'Unknown time'} • {event?.ipAddress || 'Unknown IP'}
                              </Typography>
                            }
                          />
                        </ListItem>
                        {index < safeLength(securityEvents) - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            )}

            {/* MFA Management Tab */}
            {activeTab === 1 && (
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#003366', fontWeight: 600 }}>
                  MFA Management
                </Typography>

                <Alert severity="info" sx={{ mb: 2 }}>
                  Use the Profile page to manage your MFA settings securely.
                </Alert>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    window.location.href = '/profile';
                  }}
                  sx={{ bgcolor: '#003366' }}
                >
                  Go to Profile → MFA Settings
                </Button>
              </Box>
            )}

            {/* Password Management Tab */}
            {activeTab === 2 && (
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#003366', fontWeight: 600 }}>
                  Password Management
                </Typography>

                <Alert severity="info" sx={{ mb: 2 }}>
                  Your password was last changed on {safeDateFormat(securityStatus?.passwordLastChanged)}.
                </Alert>

                <Button variant="outlined" color="primary">
                  Change Password
                </Button>
              </Box>
            )}
          </Paper>
        </>
      )}
    </Container>
  );
};

export default SecurityDashboard;
